import { Injectable, signal } from '@angular/core';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

export interface Alert {
  id: number;
  message: string;
  type: AlertType;
  actionText?: string;
  cancelText?: string;
  onAction?: () => void;
  onCancel?: () => void;

  // Review prompt specific fields
  isReviewPrompt?: boolean;
  reviewText?: string;
  onYes?: () => void;
  onNo?: () => void;
  vendorDisplayName?: string;

  // Needed for POST /shopasst/api/google-review/track
  purchaseId?: string;
  reviewId?: string;
  ratingAtPrompt?: number;
}

@Injectable({ providedIn: 'root' })
export class AlertService {
  alerts = signal<Alert[]>([]);
  private counter = 0;

  show(
    message: string,
    type: AlertType = 'success',
    duration = 3000,
    options?: {
      actionText?: string;
      cancelText?: string;
      onAction?: () => void;
      onCancel?: () => void;
    }
  ): void {
    const id = ++this.counter;

    this.alerts.update(t => [
      ...t,
      {
        id,
        message,
        type,
        actionText: options?.actionText,
        cancelText: options?.cancelText,
        onAction: options?.onAction,
        onCancel: options?.onCancel
      }
    ]);

    if (!options?.actionText && !options?.cancelText) {
      setTimeout(() => this.remove(id), duration);
    }
  }

  // ── New: show the copy/Google-review prompt (no auto-dismiss) ──
  showReviewPrompt(
    reviewText: string,
    onYes: () => void,
    onNo: () => void,
    vendorDisplayName?: string,
    purchaseId?: string,
    reviewId?: string,
    ratingAtPrompt?: number
  ): void {
    const id = ++this.counter;
    this.alerts.update(t => [
      ...t,
      {
        id, message: '', type: 'info', isReviewPrompt: true,
        reviewText, onYes, onNo, vendorDisplayName,
        purchaseId, reviewId, ratingAtPrompt
      }
    ]);
  }

  triggerAction(alert: Alert):void {
    alert.onAction?.();
    this.remove(alert.id);
  }

  remove(id: number): void {
    this.alerts.update(t => t.filter(alert => alert.id !== id));
  }

  triggerCancel(alert: Alert): void {
    alert.onCancel?.();
    this.remove(alert.id);
  }
}